const SPIN_DURATION_SECONDS = 8;
const EASE_X1 = 0.32;
const EASE_Y1 = 0;
const EASE_X2 = 0.24;
const EASE_Y2 = 1;

let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  const AudioCtx =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioCtx) {
    return null;
  }
  if (!audioContext) {
    audioContext = new AudioCtx();
  }
  return audioContext;
};

export const unlockWheelAudio = async () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    await ctx.resume();
  }
};

const bezierCoord = (t: number, p1: number, p2: number) => {
  const remaining = 1 - t;
  return (
    3 * remaining * remaining * t * p1 +
    3 * remaining * t * t * p2 +
    t * t * t
  );
};

const bezierDerivative = (t: number, p1: number, p2: number) => {
  const remaining = 1 - t;
  return (
    3 * remaining * remaining * p1 +
    6 * remaining * t * (p2 - p1) +
    3 * t * t * (1 - p2)
  );
};

const spinEase = (timeProgress: number) => {
  let guess = timeProgress;
  for (let i = 0; i < 8; i += 1) {
    const currentX = bezierCoord(guess, EASE_X1, EASE_X2);
    const slope = bezierDerivative(guess, EASE_X1, EASE_X2);
    if (Math.abs(slope) < 1e-6) {
      break;
    }
    guess = Math.min(1, Math.max(0, guess - (currentX - timeProgress) / slope));
  }
  return bezierCoord(guess, EASE_Y1, EASE_Y2);
};

const scheduleTick = (
  ctx: AudioContext,
  destination: AudioNode,
  time: number,
  progress: number,
) => {
  const oscillator = ctx.createOscillator();
  const tickGain = ctx.createGain();
  oscillator.type = "square";
  oscillator.frequency.setValueAtTime(1600 - progress * 900, time);
  tickGain.gain.setValueAtTime(0.045, time);
  tickGain.gain.exponentialRampToValueAtTime(0.001, time + 0.035);
  oscillator.connect(tickGain);
  tickGain.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + 0.04);
};

const scheduleWinChime = (
  ctx: AudioContext,
  destination: AudioNode,
  time: number,
) => {
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((frequency, index) => {
    const noteTime = time + index * 0.12;
    const oscillator = ctx.createOscillator();
    const noteGain = ctx.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(frequency, noteTime);
    noteGain.gain.setValueAtTime(0.12, noteTime);
    noteGain.gain.exponentialRampToValueAtTime(0.001, noteTime + 0.28);
    oscillator.connect(noteGain);
    noteGain.connect(destination);
    oscillator.start(noteTime);
    oscillator.stop(noteTime + 0.3);
  });
};

export const playWheelSpinSound = ({
  duration = SPIN_DURATION_SECONDS,
  sliceCount,
  totalRotation,
}: {
  duration?: number;
  sliceCount: number;
  totalRotation: number;
}): (() => void) => {
  const ctx = getAudioContext();
  if (!ctx || sliceCount < 1 || totalRotation <= 0) {
    return () => undefined;
  }

  void ctx.resume();
  const spinGain = ctx.createGain();
  spinGain.gain.setValueAtTime(1, ctx.currentTime);
  spinGain.connect(ctx.destination);

  const startTime = ctx.currentTime;
  const degreesPerSlice = 360 / sliceCount;
  let lastSlice = 0;
  let lastTickTime = startTime - 1;
  const sampleCount = 800;

  for (let sample = 1; sample <= sampleCount; sample += 1) {
    const timeProgress = sample / sampleCount;
    const rotation = spinEase(timeProgress) * totalRotation;
    const slice = Math.floor(rotation / degreesPerSlice);
    if (slice <= lastSlice) {
      continue;
    }
    lastSlice = slice;
    const tickTime = startTime + timeProgress * duration;
    if (tickTime - lastTickTime < 0.022) {
      continue;
    }
    lastTickTime = tickTime;
    const tickProgress = Math.min(
      1,
      slice / Math.max(1, totalRotation / degreesPerSlice),
    );
    scheduleTick(ctx, spinGain, tickTime, tickProgress);
  }

  scheduleWinChime(ctx, spinGain, startTime + duration);

  return () => {
    const now = ctx.currentTime;
    spinGain.gain.cancelScheduledValues(now);
    spinGain.gain.setValueAtTime(0.0001, now);
    spinGain.disconnect();
  };
};

export { SPIN_DURATION_SECONDS };
